import { IApplicationLeaseQuote } from '../Interfaces/Application/IApplicationLeaseQuote';
import { ILeaseAmortizationSchedulePayment } from '../Interfaces/Application/ILeaseAmortizationSchedulePayment';

export interface LeaseScheduleRow extends Omit<ILeaseAmortizationSchedulePayment, 'paymentDate'> {
  paymentDate: Date;
}

const roundCents = (value: number) => Math.round(value * 100) / 100;

const money = (value: number) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** The quote's schedule by payment number, with dates as Date objects (sent as calendar dates without a time zone). */
export const getLeaseScheduleRows = (quote: IApplicationLeaseQuote | undefined): LeaseScheduleRow[] =>
  [...(quote?.amortizationSchedulePayments ?? [])]
    .sort((a, b) => a.paymentNumber - b.paymentNumber)
    .map(row => ({ ...row, paymentDate: new Date(row.paymentDate) }));

/**
 * A note when the rows do not add up to the Solve's totals. Every row repeats the same depreciation and rent charge,
 * as AMS does: rounding to the cent, or a tax from the rates that differs from the Solve's, leaves a difference.
 */
export const getLeaseScheduleNote = (quote: IApplicationLeaseQuote | undefined, rows: LeaseScheduleRow[]): string | undefined => {
  const last = rows[rows.length - 1];
  if (!quote || !last) {
    return undefined;
  }
  const totals: { name: string; scheduled: number; total?: number }[] = [
    { name: 'depreciation', scheduled: last.depreciationToDate, total: quote.totalDepreciation },
    { name: 'rent charge', scheduled: last.rentChargeToDate, total: quote.totalRentCharge },
  ];
  const differences = totals
    .filter(({ scheduled, total }) => total != null && roundCents(scheduled) !== roundCents(total))
    .map(({ name, scheduled, total }) => `${name} adds up to ${money(scheduled)} (Solve total ${money(total!)})`);
  return differences.length > 0 ? `The schedule's ${differences.join(' and ')}.` : undefined;
};
